// src/pages/AdminPage/components/AdminSidebar.tsx
import { useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  BookOpen,
  Folder,
  LogOut,
  ChevronRight,
  X,
  Bell,
  Video,
  Flag,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import type { AdminTab } from "../utils/adminTypes";

interface AdminSidebarProps {
  tab: AdminTab;
  setTab: (tab: AdminTab) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const navItems: { key: AdminTab; icon: any; label: string }[] = [
  { key: "dashboard", icon: Home, label: "Dashboard" },
  { key: "users", icon: Users, label: "Foydalanuvchilar" },
  { key: "topics", icon: Folder, label: "Mavzular" },
  { key: "questions", icon: BookOpen, label: "Savollar" },
  { key: "notifications", icon: Bell, label: "Bildirishnomalar" },
  { key: "videos", icon: Video, label: "Videolar" },
  { key: "reports", icon: Flag, label: "Shikoyatlar" },
];

export default function AdminSidebar({
  tab,
  setTab,
  isMobileOpen,
  onMobileClose,
}: AdminSidebarProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const renderNavItems = (onClick?: () => void) =>
    navItems.map((item) => {
      const Icon = item.icon;
      const active = tab === item.key;
      return (
        <div
          key={item.key}
          className="nav-item"
          onClick={() => {
            setTab(item.key);
            onClick?.();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "11px 14px",
            borderRadius: 12,
            marginBottom: 4,
            background: active ? "rgba(59,130,246,.12)" : "transparent",
            border:
              active ?
                "1px solid rgba(59,130,246,.2)"
              : "1px solid transparent",
          }}>
          <Icon
            size={17}
            style={{
              color: active ? "#3B82F6" : "#475569",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              color: active ? "#fff" : "#64748B",
              fontWeight: active ? 700 : 500,
              fontSize: 13,
            }}>
            {item.label}
          </span>
          {active && (
            <ChevronRight
              size={14}
              style={{ color: "#3B82F6", marginLeft: "auto" }}
            />
          )}
        </div>
      );
    });

  return (
    <>
      {/* DESKTOP */}
      <div
        className="sidebar-desktop"
        style={{
          width: 240,
          background: "#0D1526",
          borderRight: "1px solid #1E2D45",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 50,
        }}>
        {/* Logo */}
        <div
          style={{
            padding: "20px 20px 16px",
            borderBottom: "1px solid #1E2D45",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36 }}>
              <img
                src="/logo.png"
                alt="AvtoMind"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <div>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 13,
                  letterSpacing: 0.5,
                }}>
                AVTOMIND
              </div>
              <div
                style={{
                  color: "#EF4444",
                  fontWeight: 700,
                  fontSize: 9,
                  letterSpacing: 2,
                }}>
                ADMIN
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, padding: "12px 10px", overflowY: "auto" }}>
          {renderNavItems()}
        </div>

        {/* User + Logout */}
        <div style={{ padding: "12px 10px", borderTop: "1px solid #1E2D45" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 12,
              background: "#111827",
              marginBottom: 8,
            }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#3B82F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 13,
                flexShrink: 0,
              }}>
              {user?.fullName?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {user?.fullName || "Admin"}
              </div>
              <div style={{ color: "#EF4444", fontSize: 10, fontWeight: 600 }}>
                Admin
              </div>
            </div>
          </div>
          <div
            className="nav-item"
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(239,68,68,.2)",
              background: "rgba(239,68,68,.05)",
            }}>
            <LogOut size={15} style={{ color: "#EF4444" }} />
            <span style={{ color: "#EF4444", fontWeight: 600, fontSize: 13 }}>
              Chiqish
            </span>
          </div>
        </div>
      </div>

      {/* MOBILE */}
      {isMobileOpen && (
        <div
          className="mobile-overlay"
          style={{ position: "fixed", inset: 0, zIndex: 100 }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.6)",
            }}
            onClick={onMobileClose}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 240,
              background: "#0D1526",
              borderRight: "1px solid #1E2D45",
              display: "flex",
              flexDirection: "column",
            }}>
            <div
              style={{
                padding: "16px",
                borderBottom: "1px solid #1E2D45",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
                AvtoMind Admin
              </span>
              <button
                onClick={onMobileClose}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#64748B",
                  cursor: "pointer",
                }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, padding: "12px 10px" }}>
              {renderNavItems(onMobileClose)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
